/**
 * Utility functions for table analytics tracking
 */
export var SortDirection;
(function (SortDirection) {
    SortDirection["ASCENDING"] = "ascending";
    SortDirection["DESCENDING"] = "descending";
    SortDirection["UNKNOWN"] = "unknown";
})(SortDirection || (SortDirection = {}));
export var SortKeywords;
(function (SortKeywords) {
    SortKeywords["ASCENDING"] = "ascending";
    SortKeywords["DESCENDING"] = "descending";
    SortKeywords["ASC"] = "asc";
    SortKeywords["DESC"] = "desc";
})(SortKeywords || (SortKeywords = {}));
/**
 * Determines if an action is a sorting action based on label or ID
 * @param actionLabel - The label of the action
 * @param actionId - The ID of the action
 * @returns True if this is a sorting action, false otherwise
 */
export const isSortingAction = (actionLabel, actionId) => {
    const label = actionLabel.toLowerCase();
    const id = actionId.toLowerCase();
    // Check if this is a sorting action
    return (label.includes(SortKeywords.ASCENDING) ||
        label.includes(SortKeywords.DESCENDING) ||
        label.includes(SortKeywords.ASC) ||
        label.includes(SortKeywords.DESC) ||
        id.includes(SortKeywords.ASC) ||
        id.includes(SortKeywords.DESC) ||
        label.includes('sort'));
};
/**
 * Extracts sort direction from action label or ID
 * @param actionLabel - The label of the action (e.g., "Sort Ascending")
 * @param actionId - The ID of the action
 * @returns The sort direction as a string
 */
export const extractSortDirection = (actionLabel, actionId) => {
    const label = actionLabel.toLowerCase();
    const id = actionId.toLowerCase();
    // Check for ascending/descending in label first (more reliable)
    if (label.includes(SortKeywords.ASCENDING) ||
        label.includes(SortKeywords.ASC)) {
        return SortDirection.ASCENDING;
    }
    if (label.includes(SortKeywords.DESCENDING) ||
        label.includes(SortKeywords.DESC)) {
        return SortDirection.DESCENDING;
    }
    // Fallback to checking action ID
    if (id.includes(SortKeywords.ASC)) {
        return SortDirection.ASCENDING;
    }
    if (id.includes(SortKeywords.DESC)) {
        return SortDirection.DESCENDING;
    }
    // If no sort direction found, return the original action identifier
    return actionId || actionLabel || SortDirection.UNKNOWN;
};
/**
 * Determines the column name from column definition
 * @param colDef - The column definition object
 * @returns The column name/identifier
 */
export const extractColumnName = (colDef) => {
    return (colDef === null || colDef === void 0 ? void 0 : colDef.field) || (colDef === null || colDef === void 0 ? void 0 : colDef.colId) || SortDirection.UNKNOWN;
};
/**
 * Wrapper function to enhance action handlers with analytics tracking
 * @param originalAction - The original action object
 * @param columnName - The column name for analytics
 * @param handleSortingAction - Function to handle sorting analytics
 * @returns Enhanced action with analytics tracking
 */
export const wrapActionWithAnalytics = (originalAction, columnName, handleSortingAction) => {
    if (!originalAction || !originalAction.onClick) {
        return originalAction;
    }
    return {
        ...originalAction,
        onClick: (event) => {
            // Only track sorting analytics for actual sorting actions
            const actionLabel = originalAction.label || '';
            const actionId = originalAction.id || '';
            if (isSortingAction(actionLabel, actionId)) {
                const sortBy = extractSortDirection(actionLabel, actionId);
                handleSortingAction(sortBy, columnName);
            }
            // Call the original action handler
            originalAction.onClick(event);
        },
    };
};
/**
 * Wrapper function to enhance action groups with analytics tracking
 * @param actionGroups - Array of action groups
 * @param columnName - The column name for analytics
 * @param handleSortingAction - Function to handle sorting analytics
 * @returns Enhanced action groups with analytics tracking
 */
export const wrapActionGroupsWithAnalytics = (actionGroups, columnName, handleSortingAction) => {
    if (!actionGroups || !Array.isArray(actionGroups)) {
        return actionGroups;
    }
    return actionGroups.map((group) => {
        var _a;
        return ({
            ...group,
            items: ((_a = group.items) === null || _a === void 0 ? void 0 : _a.map((item) => wrapActionWithAnalytics(item, columnName, handleSortingAction))) || [],
        });
    });
};
//# sourceMappingURL=list-table.util.js.map